import { createWebHistory, createRouter, START_LOCATION } from 'vue-router'
import store from '../store/index.js'
import Welcome from './routes/Welcome.vue'
import SignIn from './routes/SignIn.vue'
import ResetPasswordValidation from './routes/ResetPasswordValidation.vue'
import Portal from './routes/Portal.vue'
import PortalBoardMeetings from './routes/PortalBoardMeetings.vue'
import PortalDashboard from './routes/PortalDashboard.vue'
import PortalBoardResources from './routes/PortalBoardResources.vue'
import PortalResources from './routes/PortalResources.vue'
import PortalHelp from './routes/PortalHelp.vue'
import PortalSettings from './routes/PortalSettings.vue'
import ElectionsPortal from './routes/elections/ElectionsPortal.vue'
import ElectionsHome from './routes/elections/Home.vue'
import ElectionsVoting from './routes/elections/ElectionVoting.vue'

const getSchoolById = (to) => {
  const schoolId = Number.parseInt(to.params.id) || null
  const userSchools = store.getters['portal/getSchools']
  const defaultSchool = userSchools?.[0]

  const school = schoolId
    ? userSchools.find((school) => school.id === schoolId) || defaultSchool
    : defaultSchool

  return school
}

const getSchoolBySlug = (to) => {
  const schoolSlug = to.params.clientSlug || null
  const userSchools = store.getters['elections/getSchools']
  const defaultSchool = userSchools?.[0]

  const school = schoolSlug
    ? userSchools.find((school) => school.slug === schoolSlug) || defaultSchool
    : defaultSchool

  return school
}

const setCurrentElection = () => {
  // If there's only one election, set it to active
  const elections = store.getters['elections/getSchoolElections']
  // @TODO : this is causing election id not to be set in the route
  // params meaning submit nomination button does not work
  // if (elections.length === 1) {
  //   const electionId = elections[0].id
  //   store.dispatch('elections/dispatchUpdateCurrentElection', electionId)
  // }
}

/**
 * Election route guard with malformed slug fallback support
 * 
 * This function handles the complexity of supporting both slug-based and election ID-based routing.
 * 
 * Routing scenarios:
 * 1. Valid slug (/elections/te-parito-kowhai-school/123/vote) → Normal flow
 * 2. Invalid/malformed slug with election ID (/elections/te-parito-k?whai-school/123/vote) → ID fallback
 * 3. School browsing without election (/elections/te-parito-kowhai-school) → Normal flow for school selection
 * 
 * Why this complexity exists:
 * - Printed QR codes contain malformed slugs due to special character encoding issues
 * - Users still need to access elections from these printed materials
 * - Maintaining backwards compatibility with existing URL patterns
 * - Allowing school browsing without specific election selection
 * 
 * Future improvement: Standardize all direct election links to use election ID as primary identifier
 */
const beforeEnterElections = async (to, from, next, actions = []) => {
  const schoolSlugCurrent = to.params.clientSlug
  let electionId = to.params.electionId
  
  // TEMPORARY FIX for printed QR codes with malformed URLs
  // 
  // ISSUE: Special characters in school names (e.g. "kōwhai") got corrupted during 
  // CSV generation/printing process, creating URLs like:
  // /elections/te-parito-k?whai-school/123/vote?votingToken=token
  // instead of: /elections/te-parito-kowhai-school/123/vote?votingToken=token
  //
  // PROBLEM: The ? character breaks Laravel/Vue Router parameter parsing:
  // - Laravel sees path: /elections/te-parito-k 
  // - Everything after ? treated as query parameters
  // - Vue Router gets broken params: {clientSlug: "te-parito-k", electionId: ""}
  //
  // SOLUTION: Extract election ID and action from full URL when params are broken
  // TODO: Remove this code once all printed materials expire and URLs are fixed
  if (!electionId) {
    const fullUrl = window.location.href
    // DEBUG: Uncomment for troubleshooting malformed URLs
    // eslint-disable-next-line no-console
    // console.log('=== MALFORMED URL HANDLER: No electionId in params ===')
    // eslint-disable-next-line no-console
    // console.log('Full URL:', fullUrl)
    
    // Look for /NUMBER/vote anywhere in the URL (voting URLs only - nominations aren't printed)
    const match = fullUrl.match(/\/(\d+)\/vote/)
    if (match) {
      electionId = match[1]
      // DEBUG: Uncomment for troubleshooting malformed URLs
      // eslint-disable-next-line no-console
      // console.log('MALFORMED URL: Extracted election ID:', electionId, 'for voting')
      
      // For malformed voting URLs, redirect to proper voting route to fix component nesting
      if (to.name !== 'schoolVote') {
        // DEBUG: Uncomment for troubleshooting malformed URLs
        // eslint-disable-next-line no-console
        // console.log('MALFORMED URL: Redirecting to clean schoolVote route')
        
        // Extract voting token from malformed query structure
        // Query looks like: {"ap/116/vote?votingToken": "token_value"}
        const votingTokenKey = Object.keys(to.query).find(key => key.includes('votingToken'))
        const cleanQuery = votingTokenKey ? { votingToken: to.query[votingTokenKey] } : {}
        
        next({ 
          name: 'schoolVote', 
          params: { 
            clientSlug: schoolSlugCurrent || 'malformed',
            electionId: electionId 
          },
          query: cleanQuery
        })
        return
      }
    } else {
      // DEBUG: Uncomment for troubleshooting malformed URLs
      // eslint-disable-next-line no-console
      // console.log('MALFORMED URL: No election ID pattern found in URL')
    }
  } else {
    // DEBUG: Uncomment for troubleshooting malformed URLs
    // eslint-disable-next-line no-console
    // console.log('ROUTER: Normal URL - found electionId in params:', electionId)
  }

  if (schoolSlugCurrent) {
    const school = getSchoolBySlug(to)
    const schoolSlugDefault = school?.slug

    const invalidSlug = schoolSlugCurrent !== schoolSlugDefault

    if (invalidSlug && electionId) {
      // FALLBACK FLOW: Malformed slug detected, try election ID approach
      // This handles printed QR codes with corrupted school names
      try {
        const contextData = await store.dispatch('elections/dispatchGetElectionContext', electionId)
        
        if (contextData && contextData.school && contextData.election) {
          // Successfully loaded context using election ID, continue without redirecting
          // This prevents broken QR codes from failing and allows voting to proceed
          for (const action of actions) {
            await store.dispatch(action)
          }
          setCurrentElection()
          next()
          return
        }
      } catch (error) {
        // Election ID fallback failed, log for debugging but continue with slug redirect
        // eslint-disable-next-line no-console
        console.warn('Failed to load election context by ID, falling back to slug redirect:', error)
      }
      
      // FALLBACK TO REDIRECT: Election ID approach failed, use traditional slug correction
      // This maintains existing behavior for cases where election ID lookup fails
      if (school) {
        const slug = schoolSlugDefault
        store.dispatch('elections/dispatchUpdateSchool', school)
        for (const action of actions) {
          await store.dispatch(action)
        }
        setCurrentElection()
        next({ name: to.name, ...to.params, clientSlug: slug })
        return
      }
    } else if (!invalidSlug) {
      // NORMAL FLOW: Valid slug, proceed with standard school-based routing
      // This is the primary path for users browsing schools and selecting elections
      store.dispatch('elections/dispatchUpdateSchool', school)
      for (const action of actions) {
        await store.dispatch(action)
      }
      setCurrentElection()
      next()
      return
    }
  }

  // Default case: no slug provided or other edge cases
  next()
}

const mapRouteDisplayOrder = (item, index) => {
  if (item.meta?.navConfig) {
    item = {
      ...item,
      meta: {
        ...item.meta,
        navConfig: {
          ...item.meta.navConfig,
          displayOrder: index,
        },
      },
    }
  }

  return item
}

const routesPortalChildren = [
  {
    path: '',
    name: 'dashboard',
    component: PortalDashboard,
    props: true,
    meta: {
      navConfig: {
        name: 'Overview',
        icon: 'dashboard',
      },
      actions: ['portal/dispatchGetMeetings'],
    },
  },
  // {
  //   path: 'board-reports',
  //   name: 'boardReports',
  //   component: PortalBoardReports,
  //   props: true,
  //   meta: {
  //     navConfig: {
  //       name: 'Board Documents',
  //       icon: 'stat',
  //     },
  //     actions: ['portal/dispatchGetReports'],
  //   },
  // },
  {
    path: 'board-meetings',
    name: 'boardMeetings',
    component: PortalBoardMeetings,
    props: true,
    meta: {
      navConfig: {
        name: 'Board Meetings',
        icon: 'calendar',
      },
      actions: ['portal/dispatchGetMeetings'],
    },
    children: [
      {
        path: 'meeting',
        name: 'boardMeeting',
        component: PortalBoardMeetings,
        props: true,
      },
    ],
  },

  {
    path: 'board-resources',
    name: 'boardResources',
    component: PortalBoardResources,
    props: true,
    meta: {
      navConfig: {
        name: 'Board Resources',
        icon: 'stat',
      },
      actions: ['portal/dispatchGetLibraryFiles'],
    },
  },
  {
    path: 'portal-resources',
    name: 'portalResources',
    component: PortalResources,
    props: true,
    meta: {
      navConfig: {
        name: 'Portal Resources',
        icon: 'board',
      },
      actions: [],
    },
  },
  {
    path: 'help',
    name: 'portalHelp',
    component: PortalHelp,
    props: true,
    meta: {
      navConfig: {
        name: 'Help & Guides',
        icon: 'help',
      },
      actions: [],
    },
  },
  {
    path: 'settings',
    name: 'settings',
    component: PortalSettings,
    props: true,
    meta: {
      settings: true,
    },
    children: [
      {
        path: 'my-board',
        name: 'myBoard',
        props: true,
        component: PortalSettings,
        meta: {
          childComponent: 'SettingsMyBoard',
          isChild: true,
          actions: ['portal/dispatchGetMembers', 'portal/dispatchGetOwners'],
        },
      },
      {
        path: 'add-board-member',
        name: 'addBoardMember',
        props: true,
        component: PortalSettings,
        meta: {
          childComponent: 'SettingsAddBoardMember',
          isChild: true,
          actions: ['portal/dispatchGetMembers'],
        },
      },
    ],
  },
].map(mapRouteDisplayOrder)

const routesElectionsChildren = [
  {
    path: ':clientSlug?/:electionId?/vote',
    name: 'schoolVote',
    component: ElectionsVoting,
    props: true,
    meta: {},
    beforeEnter: (to, from, next) =>
      beforeEnterElections(to, from, next, [
        'elections/dispatchUpdateSchoolElections',
        'elections/dispatchUpdateNationalities',
      ]),
  },
  {
    path: ':clientSlug?/:electionId?/nominate',
    name: 'schoolNominate',
    component: ElectionsHome,
    props: true,
    meta: {
      statusWhitelist: ['nominations_open'],
    },
    beforeEnter: (to, from, next) =>
      beforeEnterElections(to, from, next, [
        'elections/dispatchUpdateSchoolElections',
        'elections/dispatchUpdateNationalities',
      ]),
  },
  {
    path: ':clientSlug?/:electionId?',
    name: 'schoolElection',
    component: ElectionsHome,
    props: true,
    beforeEnter: (to, from, next) =>
      beforeEnterElections(to, from, next, [
        'elections/dispatchUpdateSchoolElections',
      ]),
  },
  {
    path: ':clientSlug?',
    name: 'schoolHome',
    component: ElectionsHome,
    props: true,
    meta: {},
    beforeEnter: (to, from, next) =>
      beforeEnterElections(to, from, next, [
        'elections/dispatchUpdateSchoolElections',
      ]),
  },
  {
    path: '',
    name: 'electionsHome',
    component: ElectionsHome,
    beforeEnter: (to, from, next) => {
      store.dispatch('elections/dispatchUpdateSchool')
      next()
    },
  },
]

const routes = [
  {
    path: '/signin',
    name: 'signIn',
    component: SignIn,
    props: (route) => ({
      emailQuery: route.query.email,
      resetPassword: route.query.resetPassword === 'true',
    }),
  },
  {
    path: '/reset-password',
    name: 'resetPassword',
    component: ResetPasswordValidation,
  },

  {
    path: '/elections',
    name: 'elections',
    component: ElectionsPortal,
    props: true,
    children: routesElectionsChildren,
    beforeEnter: async () => {
      await store.dispatch('elections/dispatchUpdateSchools')
    },
  },

  {
    path: '/portal/:id?',
    name: 'portal',
    component: Portal,
    props: true,
    children: routesPortalChildren,
    meta: { middlewareAuth: true },
    beforeEnter: (to, from, next) => {
      const schoolIdCurrent = Number.parseInt(to.params?.id)
      const schooldIdDefault = getSchoolById(to)?.id
      // invalid if 'id' is not a number or not one of this user's schools
      const invalidId =
        !Number.parseInt(schoolIdCurrent) ||
        schoolIdCurrent !== schooldIdDefault

      if (invalidId) {
        // if invalid, route to default school
        const id = schooldIdDefault
        next({ name: to.name, params: { id } })
      } else {
        next()
      }
    },
  },

  {
    path: '/',
    name: 'welcome',
    component: Welcome,
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

const redirectToWelcome = (next) => {
  next({ name: 'welcome' })
  return
}

router.beforeEach(async (to, from, next) => {
  window.Path = from.path

  /** Do not allow access to these routes if the user is not an administrator...  */
  if (to.matched.some((record) => record.meta.middlewareAuth)) {
    if (
      from.path !== '/signin' &&
      (from === START_LOCATION ||
        (!from.path.includes('/portal') && to.path.includes('/portal')) ||
        (from.path.includes('/admin') && to.path.includes('/portal')))
    ) {
      await auth.updateUser()
    }
    const isAuthenticated = auth.isAuthenticated()
    // Kick out if unauthenticated
    if (!isAuthenticated) {
      // not auth'd - redirect to sign in
      return redirectToWelcome(next)
    } else {
      // route has changed - make sure the current school is set correctly
      const school = getSchoolById(to)
      await store.dispatch('portal/dispatchSetSchool', school)

      // Kick out if not permitted
      if (!auth.isPermitted()) {
        return redirectToWelcome(next)
      }

      // @TODO global loader and route actions
      // if (to.meta?.actions) {
      //   await store.dispatch('global/dispatchUpdateIsLoading', true)
      // for (const action of to.meta.actions) {
      //   await store.dispatch(action)
      // }
      //   await store.dispatch('global/dispatchUpdateIsLoading')
      // }
    }
  }

  next()
})

export default router
